import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import DeleteTimeEntryButton from './DeleteTimeEntryButton';

const ListContainer = styled.div`
  width: 100%;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const Table = styled.table`
  width: 100%;
  @media (max-width: 768px) {
    display: none;
  }

  tr td {
    border-bottom: 1px solid #ececec;
  }

  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  border-radius: 12px;
  border: 1px solid #ececec;
`;

const TableHead = styled.thead`
  
  th {
    padding: 0.8rem;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ececec;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid #eee;
    
    &:last-child {
      border-bottom: none;
    }
    
    &:hover {
      background-color: #f9f9f9;
    }
    
    @media (max-width: 768px) {
      display: block;
      margin-bottom: 1rem;
      border: 1px solid #eee;
      border-radius: 4px;
      padding: 0.5rem;
    }
  }
  
  td {
    padding: 0.8rem;
    
    @media (max-width: 768px) {
      display: block;
      padding: 0.3rem 0.5rem;
      text-align: right;
      position: relative;
      
      &:before {
        content: attr(data-label);
        float: left;
        font-weight: 600;
      }
    }
  }
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${props => {
    switch (props.type) {
      case 'Pre-Event':
        return '#E3F2FD';
      case 'During Event':
        return '#E8F5E9';
      case 'Post-Event':
        return '#FFF3E0';
      case 'Miscellaneous':
        return '#F3E5F5';
      default:
        return '#EEEEEE';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'Pre-Event':
        return '#0D47A1 !important'; // Darker blue
      case 'During Event':
        return '#1B5E20 !important'; // Darker green
      case 'Post-Event':
        return '#BF360C !important'; // Darker orange
      case 'Miscellaneous':
        return '#4A148C !important'; // Darker purple
      default:
        return '#424242 !important'; // Darker gray
    }
  }};
`;

const EventLink = styled(Link)`
  color: var(--primary-green);
  text-decoration: none;
  font-weight: 500;
  border-radius: 4px;
  transition: transform 0.2s ease, background-color 0.2s ease;
  will-change: transform, background-color;

  
  &:hover {
    background-color: var(--primary-green-light);
    text-decoration: none;
  }

  @media (max-width: 768px) {
    padding: 0.25rem 0.5rem;
  }
`;

const EventText = styled.span`
  color: #666;
  font-style: italic;
  font-size: 0.9rem;
`;

const CardList = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const getBorderColor = (type) => {
  switch (type) {
    case 'Pre-Event':
      return '#1976D2';
    case 'During Event':
      return '#2E7D32';
    case 'Post-Event':
      return '#EF6C00';
    case 'Miscellaneous':
      return '#7B1FA2';
    default:
      return '#616161';
  }
};

const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  margin-bottom: 1rem;
  padding: 1.2rem 1rem;
  border-left: 4px solid ${props => getBorderColor(props.type)};
  border-top: 1px solid ${props => getBorderColor(props.type)};
  border-bottom: 1px solid ${props => getBorderColor(props.type)};
  border-right: 1px solid ${props => getBorderColor(props.type)};
  position: relative;
`;

const CardDate = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.2rem;
  padding-right: 2.5rem;
`;

const CardDuration = styled.div`
  background: #f5f5f5;
  display: inline-block;
  border-radius: 12px;
  font-size: 0.85rem;
  padding: 0.2rem 0.7rem;
  margin-left: 0.5rem;
`;

const CardField = styled.div`
  margin: 0.2rem 0;
  span {
    font-weight: 500;
    color: #888;
    margin-right: 0.5rem;
  }
`;

const TableActions = styled.td`
  padding: 0.8rem;
  text-align: center;
  width: 60px;
  
  @media (max-width: 768px) {
    display: block;
    padding: 0.3rem 0.5rem;
    text-align: right;
    position: relative;
    
    &:before {
      content: 'Actions:';
      float: left;
      font-weight: 600;
    }
  }
`;

const CardDeleteButton = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
`;

const LoadingOverlay = styled.div`
  position: relative;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
`;

const TableLoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--primary-green);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  will-change: transform;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric', 
    month: 'short', 
    day: 'numeric'
  });
};

const formatTime = (timeString) => {
  const date = new Date(timeString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatDurationFromMinutes = (minutes) => {
  if (!minutes || isNaN(minutes)) return '0 hr';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hrs} hr`;
  if (hrs === 0) return `${mins} min`;
  return `${hrs} hr ${mins} min`;
};


const TimeEntryList = ({ entries, onRefresh, currentUserId, loading = false }) => {
  const handleDelete = (deletedEntryId) => {
    onRefresh();
  };

  if (loading) {
    return (
      <LoadingOverlay>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <TableLoadingSpinner />
          <span>Loading entries...</span>
        </div>
      </LoadingOverlay>
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState>
        <p>No time entries found.</p>
      </EmptyState>
    );
  }

  return (
    <ListContainer>
      {/* Desktop Table */}
      <Table>
        <TableHead>
          <tr>
            <th>Date</th>
            <th>Start Time</th>
            <th>End Time</th>
            <th>Duration</th>
            <th>Event</th>
            <th>Category</th>
            <th>Description</th>
            <th></th>
          </tr>
        </TableHead>
        <TableBody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td data-label="Date">{entry.date ? formatDate(entry.date) : formatDate(entry.created_at)}</td>
              <td data-label="Start Time">{entry.start_time ? formatTime(entry.start_time) : ''}</td>
              <td data-label="End Time">{entry.end_time ? formatTime(entry.end_time) : ''}</td>
              <td data-label="Hours">
                {formatDurationFromMinutes(entry.duration_minutes)}
              </td>
              <td data-label="Event">
                {entry.event && entry.event.id ? (
                  <EventLink to={`/events/${entry.event.id}`}>
                    {entry.event.title}
                  </EventLink>
                ) : (
                  <EventText>No event</EventText>
                )}
              </td>
              <td data-label="Category">
                {entry.category ? <TypeBadge type={entry.category}>{entry.category}</TypeBadge> : '\u00A0'}
              </td>
              <td data-label="Description">{entry.description}</td>
              <TableActions data-label="Actions">
                <DeleteTimeEntryButton 
                  timeEntry={entry}
                  currentUserId={currentUserId}
                  onDelete={handleDelete}
                />
              </TableActions>
            </tr>
          ))}
        </TableBody>
      </Table>

      {/* Mobile Card List */}
      <CardList>
        {entries.map((entry) => (
          <Card key={entry.id} type={entry.category}>
            <CardDeleteButton>
              <DeleteTimeEntryButton 
                timeEntry={entry}
                currentUserId={currentUserId}
                onDelete={handleDelete}
              />
            </CardDeleteButton>
            
            <CardDate>
              {entry.date ? formatDate(entry.date) : formatDate(entry.created_at)}
              <CardDuration>
                {formatDurationFromMinutes(entry.duration_minutes)}
              </CardDuration>
            </CardDate>
            {entry.start_time && (
              <CardField>
                <span>Start Time:</span> {formatTime(entry.start_time)}
              </CardField>
            )}
            {entry.end_time && (
              <CardField>
                <span>End Time:</span> {formatTime(entry.end_time)}
              </CardField>
            )}
            {entry.event && entry.event.id && (
              <CardField>
                <span>Event:</span>
                <EventLink to={`/events/${entry.event.id}`}>
                  {entry.event.title}
                </EventLink>
              </CardField>
            )}
            {entry.category && (
              <CardField>
                <span>Category:</span>
                <TypeBadge type={entry.category}>{entry.category}</TypeBadge>
              </CardField>
            )}
            {/* {entry.location && (
              <CardField>
                <span>Location:</span> {entry.location}
              </CardField>
            )} */}
            {entry.description && (
              <CardField>
                <span>Description:</span> {entry.description}
              </CardField>
            )}
          </Card>
        ))}
      </CardList>
    </ListContainer>
  );
};

export default TimeEntryList; 